import {
  cmToInches,
  createPptx,
  formatNumberWithSuffix,
  generateSourceText,
  getMetricValueShift,
  getMetricGroupBounds,
  getImageUrl,
  getSourceTextBox,
  hasData,
  inchesToCm,
  layoutMetricGroupsByBoundsRightToLeft,
  TRANSLATIONS,
} from "./utils";

function mapBrandToTemplateData(brand, lang = "fr") {
  const report = brand.report || {};
  return {
    id: brand.id,
    author: brand.author || "",
    job: (lang === 'en' && brand.author_occupation_en) ? brand.author_occupation_en : (brand.author_occupation || ""),
    title: (lang === 'en' && brand.title_en) ? brand.title_en : (brand.title || ""),
    baseline: (lang === 'en' && brand.baseline_en) ? brand.baseline_en : (brand.baseline || ""),
    target: (lang === 'en' && brand.positioning_en) ? brand.positioning_en : (brand.positioning || ""),
    author_image_path: brand.author_image_path,
    presentation_image_path: brand.presentation_image_path,
    social_image_path: brand.social_image_path,
    print_copies: report.diffusion_print || 0,
    social_followers: report.social_data || 0,
  };
}

export async function generateTemplate4(brand, sourceSettings = {}, lang = "fr") {
  const data = mapBrandToTemplateData(brand, lang);
  const pptx = createPptx(brand.brand_name);
  const slide = pptx.addSlide();
  const reportData = brand.report || {};
  const t = TRANSLATIONS[lang];
  const sourceText = generateSourceText(4, sourceSettings, reportData, lang);
  const pageWidthCm = inchesToCm(13.333);
  const metricLayoutConfig = {
    zoneRightCm: pageWidthCm - 0.5,
    gapCm: 0.11,
    separatorGapCm: 0.51,
  };

  const bgUrl = getImageUrl("marketing/orange_bg2.png");

  slide.addImage({
    path: bgUrl,
    x: cmToInches(11.53),
    y: cmToInches(-5.24),
    w: cmToInches(19.81),
    h: cmToInches(30.95),
    rotate: 250,
  });

  slide.addShape("rect", {
    x: 0,
    y: 0,
    w: 13.33,
    h: 7.5,
    fill: { type: "solid", color: "FFFFFF", transparency: 25 },
    line: { type: "none" },
  });

  slide.addShape("rect", {
    x: 0,
    y: 0,
    w: 13.33,
    h: 7.5,
    fill: { type: "none" },
    line: {
      color: "D77423",
      width: 8,
    },
  });

  const fontName = "Montserrat, Arial";
  const orangeColor = "D77423";

  const renderSingleMetricGroup = (group) => {
    slide.addText(
      [
        {
          text: group.title,
          options: {
            fontSize: 18,
            bold: true,
            color: "000000",
            fontFace: fontName,
            align: "center",
          },
        },
      ],
      {
        h: cmToInches(group.titleBox.hCm),
        w: cmToInches(group.titleBox.wCm),
        x: cmToInches(group.anchorXCm + group.titleBox.offsetXCm),
        y: cmToInches(group.titleBox.yCm),
        valign: "top",
        halign: "left",
      }
    );

    slide.addText(
      [
        {
          text: group.number,
          options: {
            fontSize: 36,
            bold: true,
            color: "000000",
            fontFace: fontName,
            align: "right",
          },
        },
      ],
      {
        h: cmToInches(group.numberBox.hCm),
        w: cmToInches(group.numberBox.wCm),
        x: cmToInches(group.anchorXCm + group.numberBox.offsetXCm),
        y: cmToInches(group.numberBox.yCm),
        valign: "top",
        halign: "left",
      }
    );

    if (group.suffix) {
      slide.addText(
        [
          {
            text: group.suffix,
            options: {
              fontSize: 18,
              bold: true,
              color: "000000",
              fontFace: fontName,
              align: "left",
            },
          },
        ],
        {
          h: cmToInches(group.suffixBox.hCm),
          w: cmToInches(group.suffixBox.wCm),
          x: cmToInches(group.anchorXCm + group.suffixBox.offsetXCm),
          y: cmToInches(group.suffixBox.yCm),
          valign: "top",
          halign: "left",
        }
      );
    }

    slide.addText(
      [
        {
          text: group.unit,
          options: {
            fontSize: 10.5,
            bold: false,
            color: "000000",
            fontFace: fontName,
            align: "left",
          },
        },
      ],
      {
        h: cmToInches(group.unitBox.hCm),
        w: cmToInches(group.unitBox.wCm),
        x: cmToInches(group.anchorXCm + group.unitBox.offsetXCm),
        y: cmToInches(group.unitBox.yCm),
        valign: "top",
        halign: "left",
      }
    );

    if (group.separatorXCm !== null) {
      slide.addShape("line", {
        x: cmToInches(group.separatorXCm),
        y: cmToInches(group.separatorY),
        w: cmToInches(0),
        h: cmToInches(group.separatorHCm),
        line: {
          color: "#000000",
          width: 0.75,
        },
      });
    }
  };

  // author image
  if (data.author_image_path) {
    slide.addImage({
      path: getImageUrl(data.author_image_path),
      x: cmToInches(17.42),
      y: cmToInches(1.24),
      w: cmToInches(2.52),
      h: cmToInches(2.52),
    });
  }

  // presentation image
  slide.addImage({
    path: getImageUrl(data.presentation_image_path),
    h: cmToInches(10.87),
    w: cmToInches(8.29),
    x: cmToInches(1.29),
    y: cmToInches(1.16),
  });

  // social image
  slide.addImage({
    path: getImageUrl(data.social_image_path),
    h: cmToInches(11.17),
    w: cmToInches(5.41),
    x: cmToInches(11.03),
    y: cmToInches(0.96),
  });

  // source
  slide.addText(
    [
      {
        text: sourceText,
        options: {
          fontSize: 8,
          bold: false,
          color: "000000",
          fontFace: fontName,
        },
      },
    ],
    getSourceTextBox()
  );

  slide.addText(
    [
      {
        text: data.author,
        options: {
          fontSize: 24,
          bold: true,
          color: "000000",
          fontFace: fontName,
        },
      },
    ],
    {
      x: cmToInches(20.51),
      y: cmToInches(1.47),
      w: cmToInches(9.29),
      h: cmToInches(0.9),
      valign: "top",
      halign: "left",
    }
  );

  slide.addText(
    [
      {
        text: data.job,
        options: {
          fontSize: 18,
          bold: false,
          color: "000000",
          fontFace: fontName,
        },
      },
    ],
    {
      x: cmToInches(20.56),
      y: cmToInches(2.48),
      w: cmToInches(10.97),
      h: cmToInches(1.53),
      valign: "top",
      halign: "left",
    }
  );

  // title
  slide.addText(
    [
      {
        text: data.title,
        options: {
          fontSize: 16,
          bold: true,
          color: "ffffff",
          fontFace: fontName,
        },
      },
    ],
    {
      x: cmToInches(17.39),
      y: cmToInches(4.64),
      w: cmToInches(13.66),
      h: cmToInches(1),
      highlight: orangeColor,
      valign: "top",
      halign: "left",
    }
  );

  // baseline
  slide.addText(
    [
      {
        text: data.baseline,
        options: {
          fontSize: 12,
          bold: true,
          color: "000000",
          fontFace: fontName,
        },
      },
    ],
    {
      x: cmToInches(17.4),
      y: cmToInches(6.35),
      w: cmToInches(15),
      h: cmToInches(1),
      valign: "top",
      halign: "left",
    }
  );

  // target audience
  slide.addText(
    [
      {
        text: data.target,
        options: {
          fontSize: 12,
          bold: false,
          color: "000000",
          fontFace: fontName,
          lineSpacing: 12,
        },
      },
    ],
    {
      x: cmToInches(17.4),
      y: cmToInches(7.2),
      w: cmToInches(14.37),
      h: cmToInches(4.87),
      valign: "top",
      halign: "left",
    }
  );

  // Add reworld media connect logo
  slide.addImage({
    path: getImageUrl("marketing/reworld_media_connect_logo.png"),
    h: cmToInches(0.72),
    w: cmToInches(2.93),
    x: cmToInches(0.56),
    y: cmToInches(17.83),
  });

  // Format numbers for display
  const { number: printCopiesNum, suffix: printCopiesSuffix } =
    formatNumberWithSuffix(data.print_copies, lang);
  const { number: socialFollowersNum, suffix: socialFollowersSuffix } =
    formatNumberWithSuffix(data.social_followers, lang);
  const socialValueShiftCm = getMetricValueShift("template4", "social", socialFollowersNum);

  const metricGroupDefinitions = [
    {
      id: "social",
      visible: hasData(data.social_followers),
      title: "SOCIAL",
      number: socialFollowersNum,
      suffix: socialFollowersSuffix,
      unit: t.ABONNES_RS,
      titleBox: { offsetXCm: -0.8, yCm: 13.23, wCm: 7.74, hCm: 0.89 },
      numberBox: { offsetXCm: 0.04 + socialValueShiftCm, yCm: 15.17, wCm: 3.59, hCm: 1.8 },
      suffixBox: { offsetXCm: 3.33 + socialValueShiftCm, yCm: 15.31, wCm: 2.05, hCm: 1.03 },
      unitBox: { offsetXCm: 3.34 + socialValueShiftCm, yCm: 15.96, wCm: 4.44, hCm: 0.71 },
      leftNeighborAnchorStepCm: 5.21,
      separatorOffsetXCm: 0,
      separatorY: 14.76,
      separatorHCm: 2.61,
    },
    {
      id: "print",
      visible: hasData(data.print_copies),
      title: "PRINT",
      number: printCopiesNum,
      suffix: printCopiesSuffix,
      unit: t.EXEMPLAIRES,
      titleBox: { offsetXCm: -2.3, yCm: 13.23, wCm: 8.21, hCm: 0.89 },
      numberBox: { offsetXCm: -2.3, yCm: 15.17, wCm: 4.48, hCm: 1.8 },
      suffixBox: { offsetXCm: 1.93, yCm: 15.31, wCm: 2.05, hCm: 1.03 },
      unitBox: { offsetXCm: 1.91, yCm: 15.96, wCm: 4.44, hCm: 0.71 },
      separatorY: 14.76,
      separatorHCm: 2.61,
    },
  ].map((group) => ({
    ...group,
    bounds: getMetricGroupBounds([
      group.titleBox,
      group.numberBox,
      ...(group.suffix ? [group.suffixBox] : []),
      group.unitBox,
    ]),
  }));
  const metricGroups = layoutMetricGroupsByBoundsRightToLeft(
    metricGroupDefinitions,
    metricLayoutConfig
  );

  metricGroups.forEach(renderSingleMetricGroup);

  return pptx;
}
